import React, { useState, createContext, useContext } from 'react';
import axios from 'axios';
import { useToast } from '@chakra-ui/react';
import CustomToast from '../common/CustomToast';
import { AuthContext } from './AuthContext';
import { refreshAccessToken, getAccessToken } from '../utils/tokenRefresh';

const HttpContext = createContext();
const { Provider } = HttpContext;

const HttpProvider = ({ children }) => {
  const toast = useToast();
  const authContext = useContext(AuthContext);
  const [dashboardRender, setDashboardRender] = useState({
    location: '',
    prev: '',
  });

  function updateDashboardRenderHandler(param) {
    setDashboardRender(param);
  }

  const publicAxios = axios.create({
    baseURL:
      process.env.REACT_APP_API_URL_AXIOS ||
      window.env?.REACT_APP_API_URL_AXIOS,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning':
        (process.env.NODE_ENV || window.env?.REACT_APP_NODE_ENV) ===
        'development',
    },
  });

  const authAxios = axios.create({
    baseURL:
      process.env.REACT_APP_API_URL_AXIOS ||
      window.env?.REACT_APP_API_URL_AXIOS,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning':
        (process.env.NODE_ENV || window.env?.REACT_APP_NODE_ENV) ===
        'development',
    },
  });

  const refreshAccessTokenWithCallbacks = async () => {
    return refreshAccessToken({
      onSuccess: (token) => {
        if (sessionStorage.getItem('impersonation')) {
          authContext.setImpersonationTokens({ token });
        } else {
          authContext.setAuthTokens({ token });
        }
      },
      onError: (error) => {
        console.log('Refresh Token Error', error);
        if (!getAccessToken()) return;
        authContext.logout();
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Session Expired"
              description="You have been signed out due to inactivity, please log in again."
              onClose={props.onClose}
            />
          ),
        });
      },
    });
  };

  authAxios.interceptors.request.use(
    async (config) => {
      if (
        config.url.includes('/refresh_token') ||
        config.url.includes('/impersonate') ||
        config.url.includes('invitation')
      ) {
        return config;
      }

      let token = authContext.getAccessToken();
      if (!token || authContext.isTokenExpired(token)) {
        token = await refreshAccessTokenWithCallbacks();
        if (!token) {
          const formattedError = {
            response: {
              data: {
                code: 401,
                message: 'Session Expired',
                errors: null,
              },
            },
          };

          return Promise.reject(formattedError);
        }
      }

      config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error)
  );

  authAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/refresh_token') &&
        !originalRequest.url?.includes('/internal/v1/mfa/')
      ) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessTokenWithCallbacks();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return authAxios(originalRequest);
          }
        } catch (refreshError) {
          console.error('Retry failed:', refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return (
    <Provider
      value={{
        authAxios,
        publicAxios,
        dashboardRender,
        updateDashboardRender: updateDashboardRenderHandler,
      }}
    >
      {children}
    </Provider>
  );
};

export { HttpContext, HttpProvider };
